<script setup lang="ts">
import type { Graph, Myocardium } from './lib/cardioviz';
import type { BiomarkerExplanation } from './types';

import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';

import { getBiomarkerExplanation } from './biomarker-registry';
import { Anim, createGraph, createMyocardium } from './lib/cardioviz';

const props = defineProps<{
  biomarkerId: string;
}>();

const explanation = computed<BiomarkerExplanation | null>(() =>
  getBiomarkerExplanation(props.biomarkerId),
);

const currentStepIdx = ref<number>(0);

// DOM containers
const ringContainerRef = ref<HTMLElement | null>(null);
const stackContainerRef = ref<HTMLElement | null>(null);
const graphContainerRef = ref<HTMLElement | null>(null);

let ring: Myocardium | null = null;
let stackRings: Myocardium[] = [];
let graph: Graph | null = null;
let loopToken = 0;

// Territory colors matching the application and user reference
const TERRITORY_COLORS = {
  LAD: '#ef00af',
  RCA: '#0066ff',
  LCx: '#00bb44',
  Global: '#ff6700',
  Neutral: '#64748b',
};

// Segments matching user territory layout:
// Top-Right & Mid-Right: LAD (S1, S2)
// Bottom-Right & Bottom-Left: RCA (S3, S4)
// Mid-Left & Top-Left: LCx (S5, S6)
const SEGMENTS = [
  { label: 'LCx', territory: 'LCx' as const, color: TERRITORY_COLORS.LCx },
  { label: 'LCx', territory: 'LCx' as const, color: TERRITORY_COLORS.LCx },
  { label: 'RCA', territory: 'RCA' as const, color: TERRITORY_COLORS.RCA },
  { label: 'RCA', territory: 'RCA' as const, color: TERRITORY_COLORS.RCA },
  { label: 'LAD', territory: 'LAD' as const, color: TERRITORY_COLORS.LAD },
  { label: 'LAD', territory: 'LAD' as const, color: TERRITORY_COLORS.LAD },

];

const PHASES = 25;
const phaseIndices = Array.from({ length: PHASES }, (_, i) => i);

// Synthetic physiological curves across 25 phases
const rawBaseLAD = phaseIndices.map((p) => {
  const dip = 0.13 * Math.exp(-0.5 * ((p - 8) / 3.2) ** 2);
  const rise = 0.04 * Math.sin((p / PHASES) * Math.PI);
  return 1095 * (1 - dip + rise);
});

const rawBaseLCx = phaseIndices.map((p) => {
  const dip = 0.10 * Math.exp(-0.5 * ((p - 7.5) / 3.0) ** 2);
  const rise = 0.05 * Math.sin((p / PHASES) * Math.PI);
  return 1040 * (1 - dip + rise);
});

const rawBaseRCA = phaseIndices.map((p) => {
  const dip = 0.15 * Math.exp(-0.5 * ((p - 8.5) / 3.5) ** 2);
  const rise = 0.03 * Math.sin((p / PHASES) * Math.PI);
  return 975 * (1 - dip + rise);
});

const rawBaseGlobal = phaseIndices.map(
  i => (rawBaseLAD[i] + rawBaseRCA[i] + rawBaseLCx[i]) / 3,
);

// Unnormalized curves
const unnormScale = 1000;
const unnormLAD: [number, number][] = rawBaseLAD.map((v, i) => [i, +(v / unnormScale).toFixed(4)]);
const unnormRCA: [number, number][] = rawBaseRCA.map((v, i) => [i, +(v / unnormScale).toFixed(4)]);
const unnormLCx: [number, number][] = rawBaseLCx.map((v, i) => [i, +(v / unnormScale).toFixed(4)]);
const unnormGlobal: [number, number][] = rawBaseGlobal.map((v, i) => [i, +(v / unnormScale).toFixed(4)]);

// Normalized for MYOSI (starts at 1.00)
const myosiLAD: [number, number][] = rawBaseLAD.map((v, i) => [i, +(v / rawBaseLAD[0]).toFixed(4)]);
const myosiRCA: [number, number][] = rawBaseRCA.map((v, i) => [i, +(v / rawBaseRCA[0]).toFixed(4)]);
const myosiLCx: [number, number][] = rawBaseLCx.map((v, i) => [i, +(v / rawBaseLCx[0]).toFixed(4)]);
const myosiGlobal: [number, number][] = rawBaseGlobal.map((v, i) => [i, +(v / rawBaseGlobal[0]).toFixed(4)]);

// Normalized for MYOSI_GlobalED (divided by Global Phase 0)
const globalED0 = rawBaseGlobal[0];
const globalED_LAD: [number, number][] = rawBaseLAD.map((v, i) => [i, +(v / globalED0).toFixed(4)]);
const globalED_RCA: [number, number][] = rawBaseRCA.map((v, i) => [i, +(v / globalED0).toFixed(4)]);
const globalED_LCx: [number, number][] = rawBaseLCx.map((v, i) => [i, +(v / globalED0).toFixed(4)]);
const globalED_Global: [number, number][] = rawBaseGlobal.map((v, i) => [i, +(v / globalED0).toFixed(4)]);

// Ischemia / disease simulation curves for Step 5 (Interpretation)
// In MYOSI: LAD has a significantly depressed, blunted dynamic response (much lower than RCA and LCx)
const ischemicLAD_MYOSI: [number, number][] = phaseIndices.map((i) => {
  const norm = myosiLAD[i][1];
  const defect = 0.15 * Math.exp(-0.5 * ((i - 8) / 3.8) ** 2) + 0.04;
  return [i, +(norm - defect).toFixed(4)];
});

// In MYOSI_GlobalED: resting signal is lower (resting hypoperfusion) and dynamic excursion is depressed
const ischemicLAD_GlobalED: [number, number][] = phaseIndices.map((i) => {
  const norm = globalED_LAD[i][1];
  const defect = 0.18 + 0.06 * Math.exp(-0.5 * ((i - 8) / 3.5) ** 2);
  return [i, +(norm - defect).toFixed(4)];
});

const STACK_COUNT = 6;
const stackSlices = ref<{ folded: boolean; activeIdx: number }>({
  folded: false,
  activeIdx: -1,
});

const destroyVisuals = () => {
  loopToken++;
  if (ring) {
    ring.destroy();
    ring = null;
  }
  stackRings.forEach(r => r.destroy());
  stackRings = [];
  if (graph) {
    graph.destroy();
    graph = null;
  }
};

const initVisuals = () => {
  if (!ringContainerRef.value || !stackContainerRef.value || !graphContainerRef.value) return;
  destroyVisuals();

  // Single 2D ring (compact size)
  ring = createMyocardium(ringContainerRef.value, {
    segments: 6,
    size: 120,
    outerR: 58,
    thickness: 20,
    gap: 0,
    innerFrac: 0.5,
    labels: 'none',
    interactive: false,
    accent: '#00bcd4',
    center: '',
    territories: SEGMENTS.map(() => ({
      color: TERRITORY_COLORS.Neutral,
      label: '',
      value: 1,
    })),
  });

  // 3D stacked rings for Step 3: Time Curves across phases (same size as 2D ring)
  const sliceEls = stackContainerRef.value.querySelectorAll<HTMLElement>('.stack-slice-host');
  stackRings = Array.from(sliceEls).map(el =>
    createMyocardium(el, {
      segments: 6,
      size: 130,
      outerR: 58,
      thickness: 20,
      gap: 1.5,
      innerFrac: 0.5,
      labels: 'none',
      interactive: false,
      dim: 0.72,
      center: '',
      territories: SEGMENTS.map(s => ({
        color: s.color,
        label: '',
        opacity: 0.35,
      })),
    }),
  );

  // Simplified graph: compact size to allow room for the consistent myocardium
  graph = createGraph(graphContainerRef.value, {
    width: 170,
    height: 105,
    m: { t: 6, r: 8, b: 10, l: 12 },
    x: [0, PHASES - 1],
    y: [0.82, 1.14],
    xTicks: 0,
    yTicks: 0,
    xLabel: '',
    yLabel: '',
    scrub: false,
    fmtX: () => '',
    fmtY: () => '',
    fmtMk: () => '',
    series: [
      { id: 'LAD', data: unnormLAD, color: TERRITORY_COLORS.LAD, width: 2.2 },
      { id: 'RCA', data: unnormRCA, color: TERRITORY_COLORS.RCA, width: 2.2 },
      { id: 'LCx', data: unnormLCx, color: TERRITORY_COLORS.LCx, width: 2.2 },
      { id: 'Global', data: unnormGlobal, color: TERRITORY_COLORS.Global, width: 1.8, dash: '4 3' },
    ],
  });

  graph.addRegion({ y: [0.998, 1.002], color: '#94a3b8', opacity: 0.35 });
};

// Looping animations per step
const startStepLoop = async (stepIdx: number) => {
  currentStepIdx.value = stepIdx;
  const token = ++loopToken;
  if (!ring || !graph) return;

  graph.moveMarker(null, { duration: 0 });
  stackSlices.value = { folded: false, activeIdx: -1 };
  ring.highlightTerritory(null, { duration: 0 });
  ring.selectTerritory(null, { duration: 0 });
  stackRings.forEach((r) => {
    r.highlightTerritory(null, { duration: 0 });
    r.selectTerritory(null, { duration: 0 });
  });
  ring.updateData(
    SEGMENTS.map(s => ({
      color: s.color,
      label: s.territory,
      opacity: 1,
    })),
    { duration: 0 },
  );

  if (stepIdx === 0) {
    // STEP 1: Territory Grouping (continuous myocardium -> territories)
    graph.hideCurves();

    while (true) {
      if (token !== loopToken) break;
      ring.setCenter('');
      // Continuous myocardium: solid neutral color, zero gap, no labels
      await ring.animateTo(
        {
          gap: 0,
          labels: 'none',
          territories: SEGMENTS.map(() => ({
            color: TERRITORY_COLORS.Neutral,
            label: '',
            opacity: 1,
          })),
        },
        { duration: 400 },
      );
      if (token !== loopToken) break;
      await Anim.wait(600);
      if (token !== loopToken) break;

      // Split into territories with gaps, colors, and territory labels
      await ring.animateTo(
        {
          gap: 2.5,
          labels: 'label',
          territories: SEGMENTS.map(s => ({
            color: s.color,
            label: s.territory,
            opacity: 1,
          })),
        },
        { duration: 800, stagger: 0.35 },
      );
      if (token !== loopToken) break;
      await Anim.wait(1400);
    }
  }
  else if (stepIdx === 1) {
    // STEP 2: Pixel Intensity Sampling
    graph.hideCurves();
    while (true) {
      if (token !== loopToken) break;
      ring.setCenter('');
      // Show the 6 AHA segments S1..S6 with their territory colors
      await ring.animateTo(
        {
          gap: 2.5,
          labels: 'label',
          territories: SEGMENTS.map(s => ({
            color: '#00bcd4',
            label: s.label,
            opacity: 1,
          })),
        },
        { duration: 350 },
      );
      if (token !== loopToken) break;

      // Scatter dots inside segments then collapse to median
      await ring.scatter([0, 1, 2, 3, 4, 5], {
        count: 35,
        radius: 2.2,
        appear: 450,
        hold: 300,
        collapse: 550,
      });
      if (token !== loopToken) break;

      ring.setCenter('Median');
      await ring.animateTo(
        {
          territories: SEGMENTS.map(s => ({
            color: s.color,
            label: s.label,
            opacity: 1,
          })),
        },
        { duration: 350 },
      );
      if (token !== loopToken) break;
      await Anim.wait(1100);
    }
  }
  else if (stepIdx === 2) {
    // STEP 3: Cardiac Time Curves with 3D Y-axis rotated & X-stacked rings
    graph.setDomain({ y: [0.82, 1.14] }, { duration: 0 });
    stackRings.forEach((r) => {
      r.setCenter('');
    });
    await Promise.all([
      graph.setData('LAD', unnormLAD, { duration: 0 }),
      graph.setData('RCA', unnormRCA, { duration: 0 }),
      graph.setData('LCx', unnormLCx, { duration: 0 }),
      graph.setData('Global', unnormGlobal, { duration: 0 }),
    ]);

    while (true) {
      if (token !== loopToken) break;
      stackSlices.value = { folded: false, activeIdx: -1 };
      graph.hideCurves();
      graph.moveMarker(null, { duration: 0 });
      stackRings.forEach((r) => {
        r.updateData(
          SEGMENTS.map(s => ({ color: s.color, label: '', opacity: 0.32 })),
          { duration: 0 },
        );
      });

      await Anim.wait(1000);
      if (token !== loopToken) break;

      // Fan out 3D tilted rings along X
      stackSlices.value = { folded: true, activeIdx: -1 };
      await Anim.wait(1000);
      if (token !== loopToken) break;

      // Draw curves progressively
      graph.draw({ duration: 2100, sequential: false });

      for (let i = 0; i < STACK_COUNT; i++) {
        if (token !== loopToken) break;
        stackSlices.value = { folded: true, activeIdx: i };
        const phase = Math.min(PHASES - 1, i * 5);
        graph.moveMarker(phase, { series: 'LAD', duration: 260 });

        stackRings.forEach((r, idx) => {
          if (idx < i) {
            r.highlightTerritory(null, { duration: 150 });
            r.updateData(
              SEGMENTS.map(s => ({ color: s.color, label: '', opacity: 0.5 })),
              { duration: 200 },
            );
          }
          else if (idx > i) {
            r.highlightTerritory(null, { duration: 150 });
            r.updateData(
              SEGMENTS.map(s => ({ color: s.color, label: '', opacity: 0.2 })),
              { duration: 200 },
            );
          }
          if (idx === i) {
            r.setCenter(`P${idx * 4}`);
          }
        });

        // Light up territories on active phase ring
        const activeRing = stackRings[i];
        activeRing.updateData(
          SEGMENTS.map(s => ({ color: s.color, label: '', opacity: 1 })),
          { duration: 180 },
        );
        for (const grp of [[0, 1], [2, 3], [4, 5]]) {
          if (token !== loopToken) break;
          activeRing.highlightTerritory(grp, { duration: 110 });
          await Anim.wait(115);
        }
        activeRing.highlightTerritory(null, { duration: 120 });
      }
      if (token !== loopToken) break;
      await Anim.wait(2000);
      stackRings.forEach((r) => {
        r.setCenter('');
      });
    }
  }
  else if (stepIdx === 3) {
    // STEP 4: Normalization Simulation (morphs curves between raw and normalized)
    const isGlobalED = props.biomarkerId === 'MYOSI_GlobalED';
    const normLAD = isGlobalED ? globalED_LAD : myosiLAD;
    const normRCA = isGlobalED ? globalED_RCA : myosiRCA;
    const normLCx = isGlobalED ? globalED_LCx : myosiLCx;
    const normGlob = isGlobalED ? globalED_Global : myosiGlobal;

    graph.draw({ duration: 0 });

    while (true) {
      if (token !== loopToken) break;
      // State A: Raw unnormalized curves
      ring.setCenter('Raw');
      ring.updateData(
        SEGMENTS.map(s => ({ color: s.color, label: s.territory, opacity: 1 })),
        { duration: 500 },
      );

      await Promise.all([
        graph.setDomain({ y: [0.80, 1.16] }, { duration: 650 }),
        graph.setData('LAD', unnormLAD, { duration: 650 }),
        graph.setData('RCA', unnormRCA, { duration: 650 }),
        graph.setData('LCx', unnormLCx, { duration: 650 }),
        graph.setData('Global', unnormGlobal, { duration: 650 }),
        graph.moveMarker(0, { series: 'LAD', duration: 400 }),
      ]);
      if (token !== loopToken) break;
      await Anim.wait(1200);
      if (token !== loopToken) break;

      // State B: Normalized curves
      ring.setCenter(isGlobalED ? 'Glob₀' : 'Self₀');
      ring.updateData(
        SEGMENTS.map(s => ({ color: s.color, label: s.territory, opacity: 1 })),
        { duration: 650 },
      );

      await Promise.all([
        graph.setDomain({ y: isGlobalED ? [0.82, 1.12] : [0.84, 1.06] }, { duration: 850 }),
        graph.setData('LAD', normLAD, { duration: 850 }),
        graph.setData('RCA', normRCA, { duration: 850 }),
        graph.setData('LCx', normLCx, { duration: 850 }),
        graph.setData('Global', normGlob, { duration: 850 }),
        graph.moveMarker(0, { series: isGlobalED ? 'Global' : 'LAD', duration: 600 }),
      ]);
      if (token !== loopToken) break;
      await Anim.wait(1800);
    }
  }
  else if (stepIdx === 4) {
    // STEP 5: Interpretation (demonstrates disease/ischemia: LAD curve drops much lower and LAD territory is grayed out)
    const isGlobalED = props.biomarkerId === 'MYOSI_GlobalED';
    const normLAD = isGlobalED ? globalED_LAD : myosiLAD;
    const normRCA = isGlobalED ? globalED_RCA : myosiRCA;
    const normLCx = isGlobalED ? globalED_LCx : myosiLCx;
    const normGlob = isGlobalED ? globalED_Global : myosiGlobal;
    const defectLAD = isGlobalED ? ischemicLAD_GlobalED : ischemicLAD_MYOSI;

    const yDomain: [number, number] = isGlobalED ? [0.70, 1.15] : [0.72, 1.10];

    graph.draw({ duration: 0 });

    while (true) {
      if (token !== loopToken) break;
      // State A: Normal / Healthy baseline across all territories
      ring.setCenter('Normal');
      ring.updateData(
        SEGMENTS.map(s => ({
          color: s.color,
          label: s.territory,
          opacity: 1,
        })),
        { duration: 450 },
      );

      await Promise.all([
        graph.setDomain({ y: yDomain }, { duration: 600 }),
        graph.setData('LAD', normLAD, { duration: 600, color: TERRITORY_COLORS.LAD }),
        graph.setData('RCA', normRCA, { duration: 600 }),
        graph.setData('LCx', normLCx, { duration: 600 }),
        graph.setData('Global', normGlob, { duration: 600 }),
        graph.moveMarker(null, { duration: 300 }),
      ]);
      if (token !== loopToken) break;
      await Anim.wait(1500);
      if (token !== loopToken) break;

      // State B: Pathology / Ischemia (LAD curve drops significantly lower and LAD territory grays out)
      ring.setCenter('CAD');
      ring.updateData(
        SEGMENTS.map((s) => {
          if (s.territory === 'LAD') {
            return {
              color: '#475569',
              label: 'CAD',
              opacity: 0.35,
            };
          }
          return {
            color: s.color,
            label: s.territory,
            opacity: 1,
          };
        }),
        { duration: 550 },
      );

      await Promise.all([
        graph.setData('LAD', defectLAD, { duration: 800 }),
        graph.moveMarker(8, { series: 'LAD', duration: 600 }),
      ]);
      if (token !== loopToken) break;
      await Anim.wait(2200);
    }
  }
};

const nextStep = () => {
  if (!explanation.value) return;
  const next = (currentStepIdx.value + 1) % explanation.value.steps.length;
  startStepLoop(next);
};

watch(
  () => props.biomarkerId,
  async () => {
    await nextTick();
    initVisuals();
    startStepLoop(0);
  },
);

onMounted(async () => {
  await nextTick();
  initVisuals();
  startStepLoop(0);
});

onBeforeUnmount(() => {
  destroyVisuals();
});
</script>

<template>
  <div v-if="explanation" class="explainer-compact-root">
    <!-- Header with biomarker name -->
    <div class="explainer-header">
      <h3 class="biomarker-title">
        {{ explanation.name }}
      </h3>
      <p class="biomarker-subtitle">
        {{ explanation.shortDescription }}
      </p>
    </div>

    <!-- Unified Compact Container: Stepper + Visuals & Explanation side-by-side -->
    <div class="compact-container">
      <!-- Stepper Bar (Step pills + Next Step button) -->
      <div class="stepper-bar">
        <div class="step-pills">
          <button
            v-for="(step, idx) in explanation.steps"
            :key="step.id"
            type="button"
            class="step-pill"
            :class="{ active: currentStepIdx === idx }"
            @click="startStepLoop(idx)"
          >
            <span class="step-num">{{ idx + 1 }}</span>
            <span>{{ step.shortLabel.replace(/^\d+\.\s*/, '') }}</span>
          </button>
        </div>

        <button type="button" class="next-step-btn" @click="nextStep">
          Next Step
          <i class="fa-solid fa-arrow-right ml-1.5" />
        </button>
      </div>

      <!-- Main Grid: Stage Visuals (Left) + Step Explanation (Right) in the same container -->
      <div class="stage-grid">
        <!-- Left: Visual Stage -->
        <div class="stage-visuals" :class="{ 'single-ring-mode': currentStepIdx < 2 }">
          <!-- Single 2D Ring (Steps 1, 2, 4, 5) -->
          <div
            v-show="currentStepIdx !== 2"
            ref="ringContainerRef"
            class="single-ring-box"
          />

          <!-- 3D Y-Axis Tilted & X-Stacked Phase Rings (Step 3) -->
          <div
            v-show="currentStepIdx === 2"
            ref="stackContainerRef"
            class="stack-3d-stage"
            :class="{ folded: stackSlices.folded }"
          >
            <div
              v-for="n in STACK_COUNT"
              :key="n"
              class="stack-slice-wrapper"
              :class="{ active: stackSlices.activeIdx === n - 1 }"
              :style="{ '--slice-idx': n - 1 }"
            >
              <div class="stack-slice-host" />
            </div>
          </div>

          <!-- Simplified Curve Graph (Steps 3, 4, 5) -->
          <div v-show="currentStepIdx >= 2" class="graph-box-wrapper">
            <div ref="graphContainerRef" class="graph-box" />
          </div>
        </div>

        <!-- Right: Step Explanation -->
        <div class="step-explanation-box">
          <div class="step-box-header">
            <span class="step-badge">Step {{ currentStepIdx + 1 }} of {{ explanation.steps.length }}</span>
            <h4 class="step-box-title">
              {{ explanation.steps[currentStepIdx].title }}
            </h4>
          </div>
          <p class="step-box-summary">
            {{ explanation.steps[currentStepIdx].summary }}
          </p>
          <ul class="step-box-details">
            <li v-for="(detail, i) in explanation.steps[currentStepIdx].details" :key="i">
              {{ detail }}
            </li>
          </ul>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.explainer-compact-root {
  display: flex;
  flex-direction: column;
  gap: 10px;
  color: var(--md-text-primary, #e5e5e5);
  user-select: none;
}

.explainer-header {
  padding-bottom: 6px;
  border-bottom: 1px solid var(--md-outline, rgba(255, 255, 255, 0.1));
}

.biomarker-title {
  font-size: 16px;
  font-weight: 700;
  margin: 0 0 2px 0;
  color: var(--md-text-primary, #ffffff);
}

.biomarker-subtitle {
  font-size: 11.5px;
  color: var(--md-text-secondary, #b0bec5);
  margin: 0;
  line-height: 1.35;
}

/* Unified Compact Container */
.compact-container {
  display: flex;
  flex-direction: column;
  gap: 10px;
  background: var(--md-surface, #1a1a20);
  border: 1px solid var(--md-outline, rgba(255, 255, 255, 0.1));
  border-radius: 12px;
  padding: 12px 14px;
}

/* Stepper Bar */
.stepper-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  flex-wrap: wrap;
  padding-bottom: 8px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
}

.step-pills {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
}

.step-pill {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 3px 9px;
  border-radius: 6px;
  background: var(--glass-bg-secondary, rgba(255, 255, 255, 0.05));
  border: 1px solid var(--glass-border, rgba(255, 255, 255, 0.1));
  font-size: 11px;
  color: var(--md-text-secondary, #a3a3a3);
  cursor: pointer;
  transition: all 0.18s ease;
}

.step-pill:hover {
  color: var(--md-text-primary, #ffffff);
  background: rgba(255, 255, 255, 0.1);
}

.step-pill.active {
  background: rgba(0, 188, 212, 0.16);
  border-color: #00bcd4;
  color: #00bcd4;
  font-weight: 600;
}

.step-num {
  width: 15px;
  height: 15px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.1);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 9.5px;
}

.step-pill.active .step-num {
  background: #00bcd4;
  color: #000;
}

.next-step-btn {
  padding: 4px 11px;
  border-radius: 6px;
  border: 0;
  background: #00bcd4;
  color: #001419;
  font-size: 11px;
  font-weight: 600;
  cursor: pointer;
  transition: opacity 0.15s ease;
}

.next-step-btn:hover {
  opacity: 0.9;
}

/* Side-by-side Stage Grid (Visuals Left, Explanation Right) */
.stage-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px;
  align-items: center;
  min-height: 150px;
}

.stage-visuals {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  width: 100%;
}

.stage-visuals.single-ring-mode {
  justify-content: center;
}

.single-ring-box {
  width: 130px;
  height: 130px;
  flex-shrink: 0;
}

/* 3D Y-axis rotated & X-stacked myocardium rings */
.stack-3d-stage {
  position: relative;
  width: 140px;
  height: 130px;
  display: flex;
  align-items: center;
  justify-content: center;
  perspective: 500px;
  flex-shrink: 0;
}

.stack-slice-wrapper {
  position: absolute;
  width: 130px;
  height: 130px;
  left: 50%;
  top: 50%;
  transform-style: preserve-3d;
  transform: translate(-50%, -50%) rotateY(0deg);
  transition:
    transform 1s cubic-bezier(0.22, 1, 0.36, 1),
    filter 0.25s ease;
}

.stack-3d-stage.folded .stack-slice-wrapper {
  transform: translate(calc(-50% + (var(--slice-idx) - 2.5) * 16px), -50%) rotateY(62deg);
}

.stack-3d-stage.folded .stack-slice-wrapper.active {
  transform: translate(calc(-50% + (var(--slice-idx) - 2.5) * 16px), -54%) rotateY(62deg) scale(1.06);
  filter: drop-shadow(0 0 6px rgba(0, 188, 212, 0.55));
  z-index: 10;
}

.stack-slice-host {
  width: 100%;
  height: 100%;
}

/* Simplified Graph Container */
.graph-box-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
}

.graph-box {
  width: 100%;
  max-width: 180px;
}

/* Step Explanation Box (Right Column) */
.step-explanation-box {
  background: var(--glass-bg-secondary, rgba(255, 255, 255, 0.035));
  border: 1px solid var(--glass-border, rgba(255, 255, 255, 0.08));
  border-radius: 10px;
  padding: 10px 12px;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.step-box-header {
  display: flex;
  align-items: center;
  gap: 7px;
}

.step-badge {
  font-size: 9.5px;
  font-weight: 700;
  text-transform: uppercase;
  padding: 1.5px 6px;
  border-radius: 4px;
  background: rgba(0, 188, 212, 0.15);
  color: #00bcd4;
}

.step-box-title {
  font-size: 13px;
  font-weight: 600;
  margin: 0;
  color: var(--md-text-primary, #ffffff);
}

.step-box-summary {
  font-size: 11.5px;
  color: var(--md-text-primary, #e2e8f0);
  margin: 2px 0 4px 0;
  line-height: 1.35;
}

.step-box-details {
  margin: 0;
  padding-left: 16px;
  font-size: 11px;
  color: var(--md-text-secondary, #94a3b8);
  line-height: 1.4;
}
</style>
